import { ReactNode } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { cn } from '../../lib/utils'

export type SortDir = 'asc' | 'desc'
export interface SortState { key: string; dir: SortDir }

export interface Column<T> {
  key: string
  header: ReactNode
  align?: 'left' | 'right' | 'center'
  width?: string
  cellClassName?: string
  render: (row: T) => ReactNode
  /** Presence makes the column sortable; returns the value to sort by. */
  sortAccessor?: (row: T) => string | number | boolean | Date | null | undefined
}

export interface TableGroup<T> {
  key: string
  label?: ReactNode
  count?: number
  rows: T[]
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows?: T[]
  sections?: TableGroup<T>[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  /** Per-row class hook (e.g. a selected-row tint). Merged after the base row
   *  classes via `cn`, so `bg-*` cleanly overrides the default hover. */
  rowClassName?: (row: T) => string
  empty?: ReactNode
  // Controlled sort: DataTable never reorders rows itself (sorting the whole
  // dataset is the caller's job, via useTableSort, so it composes with the
  // caller-driven pagination). DataTable only renders the indicator and reports
  // the next sort on a header click. Headers are sortable only when the column
  // has a `sortAccessor` AND `onSortChange` is provided.
  sort?: SortState | null
  onSortChange?: (next: SortState | null) => void
  className?: string
}

const TH = 'sticky top-0 z-10 bg-card text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground px-3.5 pt-3 pb-2.5 border-b border-border whitespace-nowrap'
const TD = 'px-3.5 py-3 align-middle'
const TR = 'border-b border-border/60 hover:bg-accent group transition-colors'

function alignClass(a?: 'left' | 'right' | 'center') {
  return a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left'
}

// Cycle scoped to the clicked column: asc -> desc -> off.
function nextSort(current: SortState | null | undefined, key: string): SortState | null {
  if (!current || current.key !== key) return { key, dir: 'asc' }
  if (current.dir === 'asc') return { key, dir: 'desc' }
  return null
}

function HeaderCell<T>({ column, sort, onSortChange }: {
  column: Column<T>
  sort?: SortState | null
  onSortChange?: (next: SortState | null) => void
}) {
  const sortable = !!column.sortAccessor && !!onSortChange
  const dir = sort && sort.key === column.key ? sort.dir : null
  const style = column.width ? { width: column.width } : undefined

  if (!sortable) {
    return (
      <th scope="col" className={cn(TH, alignClass(column.align))} style={style}>
        {column.header}
      </th>
    )
  }

  return (
    <th
      scope="col"
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
      className={cn(TH, alignClass(column.align))}
      style={style}
    >
      <button
        type="button"
        onClick={() => onSortChange!(nextSort(sort, column.key))}
        className={cn(
          'inline-flex items-center gap-1 rounded transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          column.align === 'right' && 'flex-row-reverse',
          dir && 'text-foreground',
        )}
      >
        <span>{column.header}</span>
        {dir === 'asc' ? <ChevronUp size={12} />
          : dir === 'desc' ? <ChevronDown size={12} />
          : <ChevronsUpDown size={12} className="opacity-30" />}
      </button>
    </th>
  )
}

// Place inside a TablePanel (a page's main full-height table) or a SectionCard
// with `noBodyPadding` (a small in-widget table). DataTable itself is only the
// <table>; the panel/card supplies the chrome.
export function DataTable<T>({ columns, rows, sections, rowKey, onRowClick, rowClassName, empty, sort, onSortChange, className }: DataTableProps<T>) {
  const groups: TableGroup<T>[] = sections ?? [{ key: '__all__', rows: rows ?? [] }]
  const totalRows = groups.reduce((n, g) => n + g.rows.length, 0)

  if (totalRows === 0 && empty != null) {
    return <>{empty}</>
  }

  return (
    <table className={cn('w-full border-collapse', className)}>
      <thead>
        <tr>
          {columns.map((c) => (
            <HeaderCell key={c.key} column={c} sort={sort} onSortChange={onSortChange} />
          ))}
        </tr>
      </thead>
      <tbody>
        {groups.map((g) => (
          <GroupRows
            key={g.key}
            group={g}
            columns={columns}
            rowKey={rowKey}
            onRowClick={onRowClick}
            rowClassName={rowClassName}
            showHeader={!!sections && g.label != null}
          />
        ))}
      </tbody>
    </table>
  )
}

function GroupRows<T>({ group, columns, rowKey, onRowClick, rowClassName, showHeader }: {
  group: TableGroup<T>
  columns: Column<T>[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
  showHeader: boolean
}) {
  return (
    <>
      {showHeader && (
        <tr>
          <td colSpan={columns.length} className="bg-muted border-b border-border px-3.5 py-2">
            <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <span>{group.label}</span>
              {group.count != null && <span className="tabular-nums">{group.count}</span>}
            </div>
          </td>
        </tr>
      )}
      {group.rows.map((row) => (
        <tr
          key={rowKey(row)}
          className={cn(TR, onRowClick && 'cursor-pointer', rowClassName?.(row))}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row) } } : undefined}
          tabIndex={onRowClick ? 0 : undefined}
        >
          {columns.map((c, i) => (
            // Inside a labelled group the first cell gets extra left padding so
            // rows read as nested under their section band.
            <td key={c.key} className={cn(TD, alignClass(c.align), showHeader && i === 0 && 'pl-7', c.cellClassName)}>
              {c.render(row)}
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}
